<script setup lang="ts">
import { onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { getCurrentLocale } from '@/i18n';
import { LANG_FRENCH } from '@/constants';
import { useHotelManagementStore } from '@/store/HotelManagementStore';
import { useAdminQuoteStore } from '@/store/AdminQuoteStore';
import { usePromoComposable } from '@/components/admin/usePromoComposable';
import { formatDate } from '@/utils/dateUtils';
import { getTextVariant } from '@/utils/stringUtils';
import { useToast } from 'primevue/usetoast';
import Accordion from 'primevue/accordion';
import AccordionContent from 'primevue/accordioncontent';
import AccordionHeader from 'primevue/accordionheader';
import AccordionPanel from 'primevue/accordionpanel';
import AutoComplete from 'primevue/autocomplete';
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import Editor from 'primevue/editor';
import TableColumn from 'primevue/column';
import HTMLEditor from '@/components/common/HTMLEditor.vue';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import LongParagraphTooltip from '@/components/modal/utils/LongParagraphTooltip.vue';

const initData = () => {
  return {
    chainName: '',
    chainNameFr: '',
    code: '',
    destCode: '',
    destName: '',
    destNameFr: '',
    groupTypes: [],
    hCode: '',
    name: '',
    nameFr: '',
    pk: null,
    sk: null
  };
};

const { t } = useI18n({ useScope: 'global' });
const currentLanguage = getCurrentLocale();

const { buttonIconExpand, buttonIconCollapse } = usePromoComposable();
const hotelSelected = ref(initData());
const HotelManagementStore = useHotelManagementStore();
const adminQuoteStore = useAdminQuoteStore();

const hotels = ref([]);
const isLoading = ref(false);
const expandedRows = ref([]);
const contractHotel = ref({});
const filterHotel = ref();
const toast = useToast();
const activePanel = ref(0);
const noteSecondExpandedRows = ref<any>({});

const getListHotel = async () => {
  await HotelManagementStore.getListHotel().then((response) => {
    hotels.value = response;
  });
};

const searchHotel = (event, lang) => {
  if (!event?.query?.trim().length) {
    filterHotel.value = [...hotels.value];
  } else {
    filterHotel.value = hotels.value.filter((hotel) => {
      if (lang === 'fr') {
        return hotel.nameFr?.toLowerCase().startsWith(event.query.toLowerCase()) || hotel.hCode?.toLowerCase().startsWith(event.query.toLowerCase()) || hotel.destNameFr?.toLowerCase().startsWith(event.query.toLowerCase());
      }
      return hotel.name?.toLowerCase().startsWith(event.query.toLowerCase()) || hotel.hCode?.toLowerCase().startsWith(event.query.toLowerCase()) || hotel.destName?.toLowerCase().startsWith(event.query.toLowerCase());
    });
  }
};

const handleSendHotel = (hotel) => {
  isLoading.value = true;
  adminQuoteStore
    .getContractHotel(hotel.hCode)
    .then((res) => {
      contractHotel.value = res;
      if (!res?.groupQuotingInfo && !res?.internalAcvNote) {
        toast.add({ severity: 'error', summary: '', detail: t('management.no_data_found'), life: 5000 });
      }
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.unexpected_error'), life: 5000 });
    })
    .finally(() => {
      isLoading.value = false;
    });
};

const formatRangeDate = (fromDate, toDate) => {
  return `${formatDate(fromDate, 'ddLLLyyyy', false, currentLanguage)} - ${formatDate(toDate, 'ddLLLyyyy', false, currentLanguage)}`;
};

const listRangeDate = (listDate) => {
  return listDate.map((date) => formatRangeDate(date.startDate, date.endDate)).join(', ');
};

const getListItemByType = (data: any, type: string) => {
  const dataFilter = data?.filter((item: any) => item?.type === type);
  if (dataFilter && dataFilter.length) {
    dataFilter.forEach((item: any, index: number) => {
      item.key = `${type}${index}`;
    });
  }
  return dataFilter || [];
};

const clearHotelSelected = () => {
  hotelSelected.value = initData();
  contractHotel.value = {};
};

onBeforeMount(() => {
  getListHotel();
});
</script>
<template>
  <LoadingSpinner v-if="isLoading" />
  <div class="card hotel-contract">
    <div>
      <div class="flex align-items-center gap-6 mb-6 search-hotel-wrapper">
        <div class="flex w-full align-items-center">
          <label for="search-hotel-contract" class="font-bold text-xl w-3"> {{ $t('hotel_contract.select_hotel_name') }} </label>
          <AutoComplete
            id="search-hotel-contract"
            v-model="hotelSelected"
            :suggestions="filterHotel"
            @complete="searchHotel($event, currentLanguage)"
            :optionLabel="getTextVariant(currentLanguage, 'name', 'nameFr')"
            completeOnFocus
            class="w-full"
            forceSelection
          >
            <template #option="slotProps">
              <div class="flex align-options-center">
                <div>
                  <div v-if="currentLanguage === LANG_FRENCH">{{ slotProps.option.hCode }} - {{ slotProps.option.nameFr }} ({{ slotProps.option.destNameFr }})</div>
                  <div v-else>{{ slotProps.option.hCode }} - {{ slotProps.option.name }} ({{ slotProps.option.destName }})</div>
                </div>
              </div>
            </template>
          </AutoComplete>
          <div>
            <i id="clearHotelSelectedId" class="pi pi-times-circle ml-2 text-red-500 cursor-pointer" @click="clearHotelSelected()"></i>
          </div>
        </div>
        <Button id="handledSendHotelId" class="submit-search-btn" @click="handleSendHotel(hotelSelected)" :disabled="!hotelSelected?.hCode" :label="$t('common.buttons.submit')"></Button>
      </div>
      <Accordion v-model:value="activePanel" v-if="contractHotel?.groupQuotingInfo" :expandIcon="'fa-light fa-3x fa-circle-plus text-primary'" :collapseIcon="'fa-light fa-3x fa-circle-minus text-primary'">
        <AccordionPanel value="0">
          <AccordionHeader>
            <div class="font-bold">{{ $t('hotel_contract.group_quote_info') }}</div>
            <div class="absolute right-0 px-3 flex align-items-center h-full expand-icon">
              <img class="button-icon" v-if="!activePanel" :src="buttonIconExpand" alt="Expand" />
              <img class="button-icon" v-else :src="buttonIconCollapse" alt="Collapse" />
            </div>
          </AccordionHeader>
          <AccordionContent>
            <Accordion :value="['0']" :multiple="true">
              <AccordionPanel value="0">
                <AccordionHeader>
                  <div class="font-bold">{{ $t('hotel_contract.group_quote_tool') }} - {{ $t('hotel_contract.complimentary_room_policy') }}</div>
                </AccordionHeader>
                <AccordionContent>
                  <DataTable :value="getListItemByType(contractHotel?.groupQuotingInfo, 'ComplimentaryRoomPolicy')" class="w-full">
                    <TableColumn field="priority" :header="$t('hotel_contract.priority')" dataType="string" class="min-w-10rem" sortable>
                      <template #body="{ data }">
                        <p class="priorityClass">{{ data.priority }}</p>
                      </template>
                    </TableColumn>
                    <TableColumn field="seasonName" dataType="string" class="min-w-10rem" sortable>
                      <template #header>
                        <span>
                          {{ `${$t('hotel_contract.seasonality')}/${$t('hotel_contract.status')}` }}
                        </span>
                      </template>
                      <template #body="{ data }">
                        <p>{{ data.seasonName }}</p>
                        <p>{{ data.status }}</p>
                      </template>
                    </TableColumn>
                    <TableColumn field="seasonStartDate" :header="$t('hotel_contract.seasonality_date')" dataType="date" class="min-w-10rem" sortable>
                      <template #body="{ data }">
                        <p>{{ formatRangeDate(data.seasonStartDate, data.seasonEndDate) }}</p>
                      </template>
                    </TableColumn>
                    <TableColumn field="createdOn" :header="$t('hotel_contract.created_on')" dataType="string" class="min-w-10rem">
                      <template #body="{ data }">
                        <p class="createdOnClass">{{ formatDate(data.createdOn, 'ddLLLyyyy', false, currentLanguage) }}</p>
                      </template>
                    </TableColumn>
                    <TableColumn field="description" :header="$t('hotel_contract.description')" dataType="string" class="min-w-10rem">
                      <template #body="{ data }">
                        <div>
                          <div><LongParagraphTooltip :text="data.description" :max-characters="40" :tooltip="data.description" label="En" /></div>
                          <div class="mt-2"><LongParagraphTooltip :text="data.frenchDescription" :max-characters="40" :tooltip="data.frenchDescription" label="Fr" /></div>
                        </div>
                      </template>
                    </TableColumn>
                    <TableColumn field="bookingFromDate" :header="$t('hotel_contract.booking_date_range')" dataType="string" class="min-w-10rem">
                      <template #body="{ data }">
                        <p class="bookingFromDateClass">{{ formatRangeDate(data.bookingFromDate, data.bookingToDate) }}</p>
                      </template>
                    </TableColumn>
                    <TableColumn field="startDate" :header="$t('hotel_contract.travel_date_range')" class="min-w-10rem">
                      <template #body="{ data }">
                        <p class="startDateClass">{{ formatRangeDate(data.startDate, data.endDate) }}</p>
                      </template>
                    </TableColumn>
                    <TableColumn field="blackoutDateRanges" :header="$t('hotel_contract.black_out_date')" dataType="string" class="min-w-10rem">
                      <template #body="{ data }">
                        <LongParagraphTooltip :text="listRangeDate(data.blackoutDateRanges)" :max-characters="45" :tooltip="listRangeDate(data.blackoutDateRanges)" />
                      </template>
                    </TableColumn>
                    <TableColumn field="basedOn" :header="$t('hotel_contract.based_on')" dataType="string" class="min-w-10rem">
                      <template #body="{ data }">
                        <p class="basedOnClass">{{ data.basedOn }}</p>
                      </template>
                    </TableColumn>
                    <TableColumn field="minGroupSize" :header="$t('hotel_contract.min_max_group')" dataType="string" class="min-w-10rem">
                      <template #body="{ data }">
                        <span class="minGroupSizeClass">{{ data.minGroupSize }} - {{ data.maxGroupSize }}</span>
                      </template>
                    </TableColumn>
                    <TableColumn field="occupancy" :header="$t('hotel_contract.occupancy')" dataType="string" class="min-w-10rem">
                      <template #body="{ data }">
                        <p class="occupancyClass">{{ data.occupancy }}</p>
                      </template>
                    </TableColumn>
                    <TableColumn field="numberOfFreeItems" :header="$t('hotel_contract.min_max_free')" dataType="string" class="min-w-10rem">
                      <template #body="{ data }">
                        <div class="numberOfFreeItemsClass1">{{ data.numberOfFreeItems }} - {{ data.maxNumberOfFreeItems }}</div>
                        <div class="numberOfFreeItemsClass2">({{ data.freeItemType }})</div>
                      </template>
                    </TableColumn>
                    <TableColumn field="minNumberOfNights" :header="$t('hotel_contract.min_night')" dataType="string" class="min-w-10rem">
                      <template #body="{ data }">
                        <p class="minNumberOfNightsClass">{{ data.minNumberOfNights }}</p>
                      </template>
                    </TableColumn>
                    <TableColumn field="combineWithEBB" :header="$t('hotel_contract.combine_with_ebb')" dataType="string" class="min-w-10rem">
                      <template #body="{ data }">
                        <p class="combineWithEBBClass">{{ data.combineWithEBB ? $t('common.yes') : $t('common.no') }}</p>
                      </template>
                    </TableColumn>
                    <TableColumn field="roomCategoryName" :header="$t('hotel_contract.room_cat_type')" dataType="string" class="min-w-10rem">
                      <template #body="{ data }">
                        <p class="roomCategoryNameClass">{{ data.roomCategoryName }}</p>
                      </template>
                    </TableColumn>
                    <TableColumn field="note" :header="$t('hotel_contract.note')" dataType="string" class="min-w-10rem">
                      <template #body="{ data }">
                        <div class="noteClass1"><LongParagraphTooltip :text="data.note" :max-characters="20" :tooltip="data.note" label="En" /></div>
                        <div class="mt-2"><LongParagraphTooltip :text="data.frenchNote" :max-characters="20" :tooltip="data.frenchNote" label="Fr" /></div>
                      </template>
                    </TableColumn>
                  </DataTable>
                </AccordionContent>
              </AccordionPanel>
              <AccordionPanel value="1">
                <AccordionHeader>
                  <div class="font-bold">{{ $t('hotel_contract.participation_in_acv') }}- {{ $t('hotel_contract.participation_in_acv') }}</div>
                </AccordionHeader>
                <AccordionContent>
                  <div class="tableClass">
                    <DataTable v-model:expandedRows="expandedRows" :value="getListItemByType(contractHotel?.groupQuotingInfo, 'ParticipationInACVPrograms')" class="w-full" dataKey="id">
                      <TableColumn :expander="true" class="w-3rem" />
                      <TableColumn field="createdOn" :header="$t('hotel_contract.created_on')" dataType="string" class="min-w-10rem">
                        <template #body="{ data }">
                          <p class="createdOnClass">{{ formatDate(data.createdOn, 'ddLLLyyyy', false, currentLanguage) }}</p>
                        </template>
                      </TableColumn>
                      <TableColumn field="conceptCategoryName" :header="$t('hotel_contract.concept_category')" dataType="string" class="min-w-10rem">
                        <template #body="{ data }">
                          <p class="conceptCategoryNameClass">{{ data.conceptCategoryName }}</p>
                        </template>
                      </TableColumn>
                      <TableColumn field="bookingFromDate" :header="$t('hotel_contract.booking_date_range')" dataType="string" class="min-w-10rem">
                        <template #body="{ data }">
                          <p class="bookingFromDateClass">{{ formatRangeDate(data.bookingFromDate, data.bookingToDate) }}</p>
                        </template>
                      </TableColumn>
                      <TableColumn field="startDate" :header="$t('hotel_contract.travel_date_range')" class="min-w-10rem">
                        <template #body="{ data }">
                          <p class="startDateClass">{{ formatRangeDate(data.startDate, data.endDate) }}</p>
                        </template>
                      </TableColumn>
                      <TableColumn field="daysOfWeek" :header="$t('hotel_contract.day_of_week')" dataType="string" class="min-w-10rem">
                        <template #body="{ data }">
                          <p class="daysOfWeekClass">{{ data.daysOfWeek }}</p>
                        </template>
                      </TableColumn>
                      <template #expansion="slotProps">
                        <div>
                          <div>
                            <Editor readonly class="w-full" v-model="slotProps.data.note">
                              <template v-slot:toolbar>
                                <div class="flex align-items-center">
                                  <div class="text-base text-600 font-bold">{{ $t('common.notes') }}</div>
                                </div>
                              </template>
                            </Editor>
                          </div>
                          <div class="mt-4">
                            <Editor readonly class="w-full" v-model="slotProps.data.frenchNote">
                              <template v-slot:toolbar>
                                <div class="flex align-items-center">
                                  <div class="text-base text-600 font-bold">{{ $t('common.french_notes') }}</div>
                                </div>
                              </template>
                            </Editor>
                          </div>
                        </div>
                      </template>
                    </DataTable>
                  </div>
                </AccordionContent>
              </AccordionPanel>
              <AccordionPanel value="2">
                <AccordionHeader>
                  <div class="font-bold">{{ $t('hotel_contract.additional_hotel_detail') }}</div>
                </AccordionHeader>
                <AccordionContent>
                  <DataTable :value="getListItemByType(contractHotel?.groupQuotingInfo, 'HotelDetails')" class="w-full" dataKey="key" responsiveLayout="stack" v-model:expandedRows="noteSecondExpandedRows">
                    <TableColumn :expander="true" class="expand-column" />
                    <TableColumn field="createdOn" :header="$t('hotel_contract.created_on')" dataType="string" class="min-w-10rem">
                      <template #body="{ data }">
                        <p class="createdOnClass">{{ formatDate(data.createdOn, 'ddLLLyyyy', false, currentLanguage) }}</p>
                      </template>
                    </TableColumn>
                    <TableColumn field="seasonName" dataType="string" class="min-w-10rem" sortable>
                      <template #header>
                        <span>
                          {{ `${$t('hotel_contract.seasonality')}/${$t('hotel_contract.status')}` }}
                        </span>
                      </template>
                      <template #body="{ data }">
                        <p>{{ data.seasonName }}</p>
                        <p>{{ data.status }}</p>
                      </template>
                    </TableColumn>
                    <TableColumn field="seasonStartDate" :header="$t('hotel_contract.seasonality_date')" dataType="date" class="min-w-10rem" sortable>
                      <template #body="{ data }">
                        <p>{{ formatRangeDate(data.seasonStartDate, data.seasonEndDate) }}</p>
                      </template>
                    </TableColumn>
                    <TableColumn field="conceptCategoryName" :header="$t('hotel_contract.concept_category')" dataType="string" class="min-w-10rem">
                      <template #body="{ data }">
                        <p class="conceptCategoryNameClass">{{ data.conceptCategoryName }}</p>
                      </template>
                    </TableColumn>
                    <TableColumn field="name" :header="$t('hotel_contract.name')" dataType="string" class="min-w-10rem">
                      <template #body="{ data }">
                        <div>
                          <div class="name1"><LongParagraphTooltip :text="data.name" :max-characters="40" :tooltip="data.description" label="En" /></div>
                          <div class="mt-2"><LongParagraphTooltip :text="data.frenchName" :max-characters="40" :tooltip="data.frenchDescription" label="Fr" /></div>
                        </div>
                      </template>
                    </TableColumn>
                    <TableColumn field="bookingFromDate" :header="$t('hotel_contract.booking_date_range')" dataType="string" class="min-w-10rem">
                      <template #body="{ data }">
                        <p class="bookingFromDateClass">{{ formatRangeDate(data.bookingFromDate, data.bookingToDate) }}</p>
                      </template>
                    </TableColumn>
                    <TableColumn field="startDate" :header="$t('hotel_contract.travel_date_range')" class="min-w-10rem">
                      <template #body="{ data }">
                        <p class="startDateClass">{{ formatRangeDate(data.startDate, data.endDate) }}</p>
                      </template>
                    </TableColumn>
                    <TableColumn field="blackoutDateRanges" :header="$t('hotel_contract.black_out_date')" dataType="string" class="min-w-10rem">
                      <template #body="{ data }">
                        <LongParagraphTooltip :text="listRangeDate(data.blackoutDateRanges)" :max-characters="45" :tooltip="listRangeDate(data.blackoutDateRanges)" />
                      </template>
                    </TableColumn>
                    <TableColumn field="minNumberOfRooms" :header="$t('hotel_contract.min_max_room')" dataType="string" class="min-w-10rem">
                      <template #body="{ data }">
                        <span class="minNumberOfRoomsClass">{{ data.minNumberOfRooms }} - {{ data.maxNumberOfRooms }}</span>
                      </template>
                    </TableColumn>
                    <TableColumn field="minGroupSize" :header="$t('hotel_contract.min_max_group')" dataType="string" class="min-w-10rem">
                      <template #body="{ data }">
                        <div>
                          <div>
                            <span>{{ data.minGroupSize }} - {{ data.maxGroupSize }}</span>
                          </div>
                          <div v-if="data.minGroupSize || data.maxGroupSize">({{ $t('common.adult') }})</div>
                        </div>
                      </template>
                    </TableColumn>
                    <TableColumn field="groupTypes" :header="$t('hotel_contract.group_type')" dataType="string" class="min-w-10rem">
                      <template #body="{ data }">
                        <div><LongParagraphTooltip :text="data?.groupTypes?.join(', ')" :max-characters="18" :tooltip="data?.groupTypes?.join(', ')" /></div>
                      </template>
                    </TableColumn>
                    <template #expansion="{ data }">
                      <label for="note-second-description" class="font-note">{{ $t('hotel_contract.note') }}</label>
                      <HTMLEditor id="note-second-description" :readonly="true" :modelValue="`<p>${$t('hotel_contract.en')}:</p>${data.note ?? ''}<p>${$t('hotel_contract.fr')}:</p>${data.frenchNote ?? ''}`" />
                    </template>
                  </DataTable>
                </AccordionContent>
              </AccordionPanel>
            </Accordion>
          </AccordionContent>
        </AccordionPanel>
      </Accordion>
      <Accordion value="0" v-if="contractHotel?.internalAcvNote" :expandIcon="'fa-light fa-3x fa-circle-plus text-primary'" :collapseIcon="'fa-light fa-3x fa-circle-minus text-primary'">
        <AccordionPanel value="1">
          <AccordionHeader>
            <div class="font-bold">{{ $t('hotel_contract.internal_acv_note') }}</div>
            <div class="absolute right-0 px-3 flex align-items-center h-full expand-icon">
              <img class="button-icon" v-if="!isTabActive" :src="buttonIconExpand" alt="Expand" />
              <img class="button-icon" v-else :src="buttonIconCollapse" alt="Collapse" />
            </div>
          </AccordionHeader>
          <AccordionContent>
            <Accordion :value="['0']" :multiple="true">
              <AccordionPanel value="0">
                <AccordionHeader>
                  <div class="font-bold">{{ $t('hotel_contract.conditions') }} - {{ $t('hotel_contract.other_conditions') }}</div>
                </AccordionHeader>
                <AccordionContent>
                  <div v-for="(condition, conditionIndex) in getListItemByType(contractHotel?.internalAcvNote, 'OtherConditions')" :key="conditionIndex">
                    <div>
                      <div v-if="condition.note">
                        <Editor readonly class="w-full mt-4" v-model="condition.note">
                          <template v-slot:toolbar>
                            <div class="flex align-items-center">
                              <div class="text-base text-600 font-bold">{{ $t('common.notes') }}</div>
                            </div>
                          </template>
                        </Editor>
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionPanel>
              <AccordionPanel value="1">
                <AccordionHeader>
                  <div class="font-bold">{{ $t('hotel_contract.contacts') }} - {{ $t('hotel_contract.group_contacts') }}</div>
                </AccordionHeader>
                <AccordionContent>
                  <DataTable :value="getListItemByType(contractHotel?.internalAcvNote, 'GroupContacts')" class="w-full">
                    <TableColumn field="groupContactType" :header="$t('hotel_contract.group_contact_type')" dataType="string" class="min-w-10rem">
                      <template #body="{ data }">
                        <p class="groupContactTypeClass">{{ data.groupContactType }}</p>
                      </template>
                    </TableColumn>
                    <TableColumn field="name" :header="$t('hotel_contract.name')" dataType="string" class="min-w-10rem">
                      <template #body="{ data }">
                        <p class="nameClass">{{ data.name }}</p>
                      </template>
                    </TableColumn>
                    <TableColumn field="title" :header="$t('hotel_contract.title')" dataType="string" class="min-w-10rem">
                      <template #body="{ data }">
                        <p class="titleClass">{{ data.title }}</p>
                      </template>
                    </TableColumn>
                    <TableColumn field="email" :header="$t('hotel_contract.email')" class="min-w-10rem">
                      <template #body="{ data }">
                        <p class="emailClass">{{ data.email }}</p>
                      </template>
                    </TableColumn>
                    <TableColumn field="phone" :header="$t('hotel_contract.phone')" dataType="string" class="min-w-10rem">
                      <template #body="{ data }">
                        <p class="phoneClass">{{ data.phone }}</p>
                      </template>
                    </TableColumn>
                  </DataTable>
                </AccordionContent>
              </AccordionPanel>
              <AccordionPanel value="2">
                <AccordionHeader>
                  <div class="font-bold">{{ $t('hotel_contract.payments') }} - {{ $t('hotel_contract.payment_note') }}</div>
                </AccordionHeader>
                <AccordionContent>
                  <div v-for="(paymentNote, paymentNoteIndex) in getListItemByType(contractHotel?.internalAcvNote, 'PaymentNotes')" :key="paymentNoteIndex">
                    <div>
                      <div v-if="paymentNote.note">
                        <Editor readonly class="w-full" v-model="paymentNote.note">
                          <template v-slot:toolbar>
                            <div class="flex align-items-center">
                              <div class="text-base text-600 font-bold">{{ $t('common.notes') }}</div>
                            </div>
                          </template>
                        </Editor>
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionPanel>
              <AccordionPanel value="3">
                <AccordionHeader>
                  <div class="font-bold">{{ $t('hotel_contract.penalties') }} - {{ $t('hotel_contract.modifications') }}</div>
                </AccordionHeader>
                <AccordionContent>
                  <div v-for="(modification, modificationIndex) in getListItemByType(contractHotel?.internalAcvNote, 'Modifications')" :key="modificationIndex">
                    <div>
                      <div v-if="modification.note">
                        <Editor readonly class="w-full" v-model="modification.note">
                          <template v-slot:toolbar>
                            <div class="flex align-items-center">
                              <div class="text-base text-600 font-bold">{{ $t('common.notes') }}</div>
                            </div>
                          </template>
                        </Editor>
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionPanel>
              <AccordionPanel value="4">
                <AccordionHeader>
                  <div class="font-bold">{{ $t('hotel_contract.others') }} - {{ $t('hotel_contract.general_notes') }}</div>
                </AccordionHeader>
                <AccordionContent>
                  <div v-for="(generalNote, generalNoteIndex) in getListItemByType(contractHotel?.internalAcvNote, 'GeneralNotes')" :key="generalNoteIndex">
                    <div>
                      <div v-if="generalNote.note">
                        <Editor readonly class="w-full" v-model="generalNote.note">
                          <template v-slot:toolbar>
                            <div class="flex align-items-center">
                              <div class="text-base text-600 font-bold">{{ $t('common.notes') }}</div>
                            </div>
                          </template>
                        </Editor>
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionPanel>
              <AccordionPanel value="5">
                <AccordionHeader>
                  <div class="font-bold">{{ $t('hotel_contract.others') }} - {{ $t('hotel_contract.add_values_or_extras') }}</div>
                </AccordionHeader>
                <AccordionContent>
                  <div v-for="(value, index) in getListItemByType(contractHotel?.internalAcvNote, 'AddedValuesOrExtras')" :key="index">
                    <div>
                      <div v-if="value.note">
                        <Editor readonly class="w-full" v-model="value.note">
                          <template v-slot:toolbar>
                            <div class="flex align-items-center">
                              <div class="text-base text-600 font-bold">{{ $t('common.notes') }}</div>
                            </div>
                          </template>
                        </Editor>
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionPanel>
            </Accordion>
          </AccordionContent>
        </AccordionPanel>
      </Accordion>
    </div>
  </div>
</template>
